import { useSearchParams } from "react-router-dom";
import SEO from "@/components/shared/SEO";
import RoomCard from "@/components/shared/RoomCard";
import RoomFilters from "@/features/rooms/components/RoomFilters";
import AvailabilityWidget from "@/components/shared/AvailabilityWidget";
import { usePublicRooms } from "@/features/rooms/hooks/usePublicRooms";
import type { RoomType } from "@/types/rooms";

function applyFilters(rooms: RoomType[], params: URLSearchParams): RoomType[] {
  let result = [...rooms];

  const guests = params.get("guests");
  if (guests) result = result.filter((r) => r.maxGuests >= Number(guests));

  const maxPrice = params.get("maxPrice");
  if (maxPrice) result = result.filter((r) => r.basePrice <= Number(maxPrice));

  const sort = params.get("sort");
  if (sort === "price-asc") result.sort((a, b) => a.basePrice - b.basePrice);
  else if (sort === "price-desc")
    result.sort((a, b) => b.basePrice - a.basePrice);
  else if (sort === "size-desc") result.sort((a, b) => b.size - a.size);
  else result.sort((a, b) => a.sortOrder - b.sortOrder); // default: featured

  return result;
}

export default function RoomsPage() {
  const { data: rooms = [], isLoading } = usePublicRooms();
  const [params] = useSearchParams();
  const filtered = applyFilters(rooms, params);

  return (
    <>
      <SEO
        title="Rooms & Suites"
        description="Explore our curated selection of rooms and suites at Hotel Metropolis, Srinagar — from intimate Deluxe Doubles to expansive Family Suites overlooking Dal Lake."
      />

      {/* Hero */}
      <section className="bg-metro-cream py-20 md:py-28">
        <div className="container-narrow text-center">
          <p className="eyebrow mb-4">Accommodation</p>
          <h1 className="mb-4 font-serif text-display-lg text-metro-green-950">
            Rooms &amp; Suites
          </h1>
          <p className="mx-auto mb-10 max-w-xl text-base leading-relaxed text-metro-ink-muted">
            Each room at Metropolis is designed to reflect the quiet luxury of
            Kashmir — natural materials, handcrafted details, and views that
            stay with you.
          </p>
          <AvailabilityWidget />
        </div>
      </section>

      {/* Listing */}
      <section className="bg-white py-16">
        <div className="container-narrow">
          {/* Filters + count */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <RoomFilters />
            {!isLoading && (
              <p className="shrink-0 text-sm text-metro-ink-subtle">
                {filtered.length}{" "}
                {filtered.length === 1 ? "room type" : "room types"}
              </p>
            )}
          </div>

          {/* States */}
          {isLoading && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-96 animate-pulse rounded-2xl bg-metro-cream-100"
                />
              ))}
            </div>
          )}

          {!isLoading && filtered.length === 0 && (
            <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
              <p className="font-serif text-xl text-metro-green-950">
                No rooms match your filters
              </p>
              <p className="text-sm text-metro-ink-subtle">
                Try adjusting your guest count or price range.
              </p>
            </div>
          )}

          {!isLoading && filtered.length > 0 && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((room) => (
                <RoomCard key={room.id} room={room} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}